<template>
  <div class="cl-wrap">
    <div class="container">

      <!-- Header -->
      <div class="header">
        <div class="version-badge">Documentation</div>
        <h1>HypnoMaker</h1>
        <div class="subtitle">Complete User Guide &nbsp;·&nbsp; v1.5</div>
      </div>

      <div class="divider"><span class="divider-glyph">✦</span></div>

      <!-- Table of Contents -->
      <div class="toc">
        <div class="toc-title">Contents</div>
        <ol>
          <li><a href="#overview">Overview</a></li>
          <li><a href="#getting-started">Getting Started</a></li>
          <li><a href="#hypno-mode">Hypno Mode</a></li>
          <li><a href="#pmv-mode">PMV Mode</a></li>
          <li><a href="#three-panel">Three-Panel Mode</a></li>
          <li><a href="#scripts">Scripts &amp; Captions</a></li>
          <li><a href="#audio">Audio &amp; Voice</a></li>
          <li><a href="#visuals">Visuals: Overlays, Filters &amp; Transitions</a></li>
          <li><a href="#output">Output Settings</a></li>
          <li><a href="#premium">Premium Features</a></li>
        </ol>
      </div>

      <!-- 1. Overview -->
      <div class="section" id="overview">
        <div class="section-title">Overview</div>
        <div class="feature feature-hero">
          <div class="feature-name">✦ What is HypnoMaker?</div>
          <p>HypnoMaker is a standalone video production tool for creating hypnosis and PMV content. Choose a script, pick a folder full of media, configure your audio and visuals, and export a finished video. No editing experience required.</p>
          <ul>
            <li><strong>Hypno Mode</strong>: guided hypnosis videos with TTS voiceover, binaural audio, and caption overlays.</li>
            <li><strong>PMV Mode</strong>: beat-synced music video editing with automatic media cutting.</li>
            <li><strong>Three-Panel Mode</strong>: layered three-panel output, available in both modes.</li>
            <li>Exports a complete, mixed video file ready to share or upload.</li>
          </ul>
        </div>
      </div>

      <!-- 2. Getting Started -->
      <div class="section" id="getting-started">
        <div class="section-title">Getting Started</div>

        <div class="feature">
          <div class="feature-name">1 · Choose a Script</div>
          <p>Browse the built-in library of 102 pre-written erotic hypnosis scripts: bimbofication, sissy, chastity, femdom, cuckold, and more. Each script contains 150–250 lines of hypnotic content. Filter by category or search by title, and hit preview to read it in full before committing. Script duration is shown before you create, so you know exactly what you're getting into.</p>
        </div>

        <div class="feature">
          <div class="feature-name">2 · Add Your Media</div>
          <p>Click <strong>Select Folder</strong> to use everything in a folder, or handpick individual files and set their exact order with the file picker. If you want a specific clip in a specific place, you can put it there. Supported formats include MP4, MOV, AVI, WEBM, JPG, PNG, GIF, and more. A count of your selected videos and images is shown before you render.</p>
        </div>

        <div class="feature">
          <div class="feature-name">3 · Configure &amp; Export</div>
          <p>Open <strong>Options</strong> to set your resolution, transitions, filters, audio levels, and more. When ready, click <strong>Create Video</strong>. The progress bar shows which stage of processing your video is at, in real time. The finished file is saved to your chosen output location.</p>
        </div>
      </div>

      <!-- 3. Hypno Mode -->
      <div class="section" id="hypno-mode">
        <div class="section-title">Hypno Mode</div>

        <div class="feature feature-hero">
          <div class="feature-name">✦ Guided Hypnosis Videos</div>
          <p>Hypno Mode builds a video around a script. The script is split into timed caption segments and spoken aloud by a TTS voice. Your media plays in the background, synced to the duration of the audio.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Script Selection</div>
          <p>Choose from the built-in script library or load a custom script file. Filter by category (pick "Affirmations" and see just the affirmation scripts) or search by title. A preview button lets you read the full script before committing.</p>
          <div class="note">Free users have access to the first several scripts. <strong>Premium</strong> unlocks the full library and custom script uploads.</div>
        </div>

        <div class="feature">
          <div class="feature-name">Caption Display</div>
          <p>Captions appear on-screen as the voiceover speaks each line. Fully configurable: toggle them on or off, change position, font, size, color, glow, and opacity.</p>
          <table class="option-table">
            <tr><th>Setting</th><th>Options</th></tr>
            <tr><td>Show Captions</td><td>On / Off</td></tr>
            <tr><td>Text Position</td><td>Center, Bottom</td></tr>
            <tr><td>Font</td><td>Any installed font (default: Impact)</td></tr>
            <tr><td>Font Size</td><td>Adjustable (default: 34pt)</td></tr>
            <tr><td>Text Color</td><td>Fully customizable</td></tr>
            <tr><td>Text Glow</td><td>0 – 20 px</td></tr>
            <tr><td>Text Opacity</td><td>50 – 100%</td></tr>
            <tr><td>Text Quality</td><td>100% (normal) – 200% (supersampled)</td></tr>
          </table>
        </div>

        <div class="feature">
          <div class="feature-name">Custom Captions</div>
          <p>Premium users can write their own scripts using the built-in editor. Custom scripts follow the same timed-caption format as the built-in library.</p>
        </div>
      </div>

      <!-- 4. PMV Mode -->
      <div class="section" id="pmv-mode">
        <div class="section-title">PMV Mode</div>

        <div class="feature feature-hero">
          <div class="feature-name">✦ Beat-Synced Music Videos</div>
          <p>PMV Mode is a full editing pipeline for Porn Music Videos. Drop in a folder of clips, select a music track, and HypnoMaker cuts the media to the beat automatically. No external software needed.</p>
          <p class="hero-note">Available exclusively to premium users.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Music Selection</div>
          <p>Choose any audio file from your computer as the backing track. The video is built to match the track's length. Volume is controlled independently via the Music Volume slider.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Beat Sync</div>
          <p>Enable <strong>Sync media cuts to beats</strong> to let HypnoMaker detect the rhythm of your track and cut media on the beat automatically. Detection is accurate on most source tracks, and falls back to time-based cuts if the rhythm can't be read.</p>
          <table class="option-table">
            <tr><th>Setting</th><th>Options</th></tr>
            <tr><td>Beat Sync</td><td>On / Off</td></tr>
            <tr><td>Beats Per Cut <em>(sync on)</em></td><td>1, 2, 4, 8 beats</td></tr>
            <tr><td>Seconds Per Cut <em>(sync off)</em></td><td>1, 2, 3, 4, 5, 8, 10 seconds</td></tr>
          </table>
          <div class="note"><strong>Beats Per Cut</strong> controls pacing. 1 = frantic, rapid cuts every beat. 8 = slow-burn, each clip holds for 8 beats before switching.</div>
        </div>

        <div class="feature">
          <div class="feature-name">Per-Video Transitions</div>
          <p>Assign different transitions to each video, randomly or in a set order, instead of applying one transition globally across the whole output.</p>
        </div>
      </div>

      <!-- 4b. Three-Panel Mode -->
      <div class="section" id="three-panel">
        <div class="section-title">Three-Panel Mode</div>

        <div class="feature feature-hero">
          <div class="feature-name">✦ Layered Three-Panel Video</div>
          <p>Both Hypno and PMV mode can output three-panel layered video: mirrored panels, visual layering, and rhythmic asymmetry. Use the classic mirrored inside/outside layout, or choose three entirely different panels.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Cut-Sync Behaviors (PMV)</div>
          <p>In PMV mode, each panel's cuts can follow a different rhythm.</p>
          <table class="option-table">
            <tr><th>Behavior</th><th>Effect</th></tr>
            <tr><td>Synchronized</td><td>All three panels cut together</td></tr>
            <tr><td>Alternating</td><td>Panels take turns cutting</td></tr>
            <tr><td>Independent</td><td>Each panel cuts on its own, pleasantly disorienting</td></tr>
          </table>
        </div>

        <div class="feature">
          <div class="feature-name">Per-Panel Source Audio</div>
          <p>When mixing source audio into the output, choose which panel's audio drives the mix. You're not locked into one source.</p>
        </div>
      </div>

      <!-- 5. Scripts -->
      <div class="section" id="scripts">
        <div class="section-title">Scripts &amp; Captions</div>

        <div class="feature">
          <div class="feature-name">Built-in Script Library</div>
          <p>HypnoMaker ships with <strong>102 hypno scripts</strong> covering a wide range of themes, every one with prerendered voiceovers in both the ElevenLabs female voices and the Fish Audio male voices. Each script contains 150–250 lines of hypnotic content. Filter the library by category to jump straight to what you want.</p>
          <div class="tags">
            <span class="tag">Bimbofication</span>
            <span class="tag">Bambi</span>
            <span class="tag">Chastity</span>
            <span class="tag">Sissy</span>
            <span class="tag">Feminization</span>
            <span class="tag">Femdom</span>
            <span class="tag">Cuckold</span>
            <span class="tag">Findom</span>
            <span class="tag">CEI</span>
            <span class="tag">Edging</span>
            <span class="tag">Breeding</span>
            <span class="tag">Pet Play</span>
            <span class="tag">Little Space</span>
            <span class="tag">Worship</span>
            <span class="tag">Furry</span>
            <span class="tag">Gooning</span>
            <span class="tag">Submission</span>
            <span class="tag">Affirmations</span>
            <span class="tag">BBC Worship</span>
            <span class="tag">Orgasm Control</span>
            <span class="tag">Drone</span>
            <span class="tag">SPH</span>
            <span class="tag">Latex</span>
            <span class="tag">Succubus</span>
            <span class="tag">Grounding</span>
          </div>
        </div>

        <div class="feature">
          <div class="feature-name">Script Preview</div>
          <p>Click the preview button next to any script to read its full content before generating the video. No surprises.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Grounding &amp; Decompression</div>
          <p>The library isn't just the ascent; it ships the off-ramp too. The White Script and the rest of the grounding material are decompression scripts with no eroticism and no kink language: they clear triggers, return the body to baseline, and give explicit permission to keep what serves you and let go of what doesn't.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Custom Script Editor</div>
          <p>Write your own scripts directly inside HypnoMaker using the built-in editor. Save and reuse them anytime. Custom scripts are loaded from your local storage and appear alongside the built-in library.</p>
          <div class="tags"><span class="tag tag-premium">Premium</span></div>
        </div>

      </div>

      <!-- 6. Audio -->
      <div class="section" id="audio">
        <div class="section-title">Audio &amp; Voice</div>

        <div class="feature">
          <div class="feature-name">TTS Voice</div>
          <p>Scripts are spoken by a text-to-speech voice. Choose from available voices in the Voice dropdown. Voiceover audio is pulled from the server on first use and cached locally, keeping the install small.</p>
          <table class="option-table">
            <tr><th>Voice</th><th>Notes</th></tr>
            <tr><td>ElevenLabs voices</td><td>Realistic, human-sounding female voices</td></tr>
            <tr><td>Fish Audio voices</td><td>Male voice option, available for all scripts</td></tr>
            <tr><td>Google TTS</td><td>Robotic, classic text-to-speech voice</td></tr>
          </table>
        </div>

        <div class="feature">
          <div class="feature-name">Bring Your Own API Key</div>
          <p>Plug in your own <strong>ElevenLabs</strong> or <strong>Fish Audio</strong> API key to voice custom scripts with any voice in their libraries. Fish Audio is substantially cheaper if you generate a lot of custom material. Key errors are surfaced clearly, so you know whether the issue is your key, your quota, or the API.</p>
          <div class="tags"><span class="tag tag-premium">Premium</span></div>
        </div>

        <div class="feature">
          <div class="feature-name">Source Audio Mixing</div>
          <p>Pull the audio off your video clips and mix it into the output, balanced against the rest of the mix with its own adjustment bar. In three-panel mode, choose which panel's audio gets mixed in. Great for diegetic layering, using a clip's own audio as texture under the script, or building rhythm off the source material itself.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Binaural Audio</div>
          <p>Binaural beats are layered beneath the voiceover to deepen the trance experience through brainwave entrainment. Volume is controlled separately from voice and music.</p>
          <table class="option-table">
            <tr><th>Type</th><th>Frequency</th><th>Effect</th></tr>
            <tr><td>Alpha</td><td>8 – 12 Hz</td><td>Relaxed, meditative focus</td></tr>
            <tr><td>Theta</td><td>4 – 8 Hz</td><td>Deep meditation, REM-adjacent</td></tr>
            <tr><td>Delta</td><td>0.5 – 4 Hz</td><td>Deep sleep state</td></tr>
          </table>
        </div>

        <div class="feature">
          <div class="feature-name">Background Music</div>
          <p>Layer background music under the voiceover. Choose a built-in preset or browse for any audio file on your computer.</p>
          <table class="option-table">
            <tr><th>Preset</th><th></th></tr>
            <tr><td>None</td><td>No background music</td></tr>
            <tr><td>Ambient</td><td>Soft, atmospheric background</td></tr>
            <tr><td>Electronic</td><td>Pulsing, electronic texture</td></tr>
            <tr><td>Custom</td><td>Any file from your computer</td></tr>
          </table>
        </div>

        <div class="feature">
          <div class="feature-name">Volume Controls</div>
          <p>Each audio layer has its own independent volume slider, all ranging from 0% to 200%.</p>
          <ul>
            <li><strong>TTS / Voice Volume</strong>: the spoken script</li>
            <li><strong>Music Volume</strong>: background music or PMV track</li>
            <li><strong>Binaural Volume</strong>: brainwave entrainment layer</li>
            <li><strong>Source Audio Volume</strong>: audio pulled from your own clips</li>
          </ul>
        </div>
      </div>

      <!-- 7. Visuals -->
      <div class="section" id="visuals">
        <div class="section-title">Visuals: Overlays, Filters &amp; Transitions</div>

        <div class="feature">
          <div class="feature-name">GIF Overlays</div>
          <p>An animated GIF is composited over your video at adjustable opacity. Choose from the built-in overlay library.</p>
          <div class="tags">
            <span class="tag">Hearts</span>
            <span class="tag">Spirals</span>
            <span class="tag">Colors</span>
            <span class="tag">Hypnotique</span>
            <span class="tag">5D</span>
            <span class="tag">Rainbow</span>
            <span class="tag">Tunnel 1</span>
            <span class="tag">Tunnel 2</span>
            <span class="tag">Twist</span>
            <span class="tag">Vortex</span>
          </div>
          <div class="note">Overlay Opacity controls how visible the GIF is over your media: 0% = invisible, 100% = fully opaque.</div>
        </div>

        <div class="feature">
          <div class="feature-name">Video Filters</div>
          <p>Apply a visual grade or effect to every frame of your video.</p>
          <table class="option-table">
            <tr><th>Filter</th><th>Effect</th></tr>
            <tr><td>None</td><td>No filter applied</td></tr>
            <tr><td>Random</td><td>A single filter is chosen randomly per clip and held for its duration</td></tr>
            <tr><td>Psychedelic</td><td>A new random filter is chosen every frame for a full strobing effect</td></tr>
            <tr><td>Sharpen &amp; Contrast</td><td>Increases sharpness and contrast for a crisp, punchy look</td></tr>
            <tr><td>Color Boost</td><td>Pushes saturation to maximum intensity</td></tr>
            <tr><td>Sepia</td><td>Warm brown-tone vintage grade</td></tr>
            <tr><td>Inverted Colors</td><td>Full RGB inversion, a psychedelic negative image</td></tr>
            <tr><td>Vignette</td><td>Darkens the edges to draw focus to the center</td></tr>
            <tr><td>B&amp;W High Contrast</td><td>Desaturated with heavy contrast boost</td></tr>
            <tr><td>Warm Grade</td><td>Orange-red tones for intimacy and warmth</td></tr>
            <tr><td>Cool Grade</td><td>Blue-purple tones for a dissociative, trance-like atmosphere</td></tr>
            <tr><td>Chromatic Aberration</td><td>RGB channel offset glitch effect</td></tr>
          </table>
        </div>

        <div class="feature">
          <div class="feature-name">Transitions</div>
          <p>Controls how HypnoMaker moves between video clips. Transitions blend <strong>video-to-video</strong>: the outgoing clip flows directly into the incoming one with no cut through black, and the actual video keeps playing throughout, never freezing on a frame.</p>
          <table class="option-table">
            <tr><th>Style</th><th>Description</th></tr>
            <tr><td>None</td><td>Hard cut between clips</td></tr>
            <tr><td>Fade</td><td>Smooth dissolve into the next clip</td></tr>
            <tr><td>Spiral</td><td>Spiral wipe into the next clip</td></tr>
            <tr><td>Pulse Zoom</td><td>Rhythmic scale pulse into the next clip</td></tr>
            <tr><td>Radial Wipe</td><td>Clock-sweep wipe reveals the incoming clip</td></tr>
            <tr><td>Breathe</td><td>Slow inhale/exhale scale into the next clip</td></tr>
            <tr><td>Tunnel</td><td>Zoom-through tunnel into the next clip</td></tr>
            <tr><td>Dissolve</td><td>Pixel-scatter dissolve into the next clip</td></tr>
            <tr><td>Luma Wipe</td><td>Luminance-based wipe using the clip's own brightness</td></tr>
            <tr><td>Luma Wipe (Inverse)</td><td>Same wipe, bright and dark areas swapped</td></tr>
          </table>
          <div class="note">Transitions apply only at actual clip boundaries. For video clips, caption/TTS line boundaries within the same clip are ignored; the transition only fires when the source media actually changes. In PMV mode, transitions can also be assigned per video, randomly or in a set order.</div>
        </div>
      </div>

      <!-- 8. Output -->
      <div class="section" id="output">
        <div class="section-title">Output Settings</div>

        <div class="feature">
          <div class="feature-name">Resolution</div>
          <p>Choose the output resolution before rendering. Higher resolutions increase render time and file size.</p>
          <table class="option-table">
            <tr><th>Preset</th><th>Dimensions</th><th>Orientation</th></tr>
            <tr><td>720p</td><td>1280 × 720</td><td>Landscape</td></tr>
            <tr><td>1080p</td><td>1920 × 1088</td><td>Landscape</td></tr>
            <tr><td>4K</td><td>3840 × 2160</td><td>Landscape</td></tr>
            <tr><td>Vertical 720p</td><td>720 × 1280</td><td>Portrait / mobile</td></tr>
            <tr><td>Vertical 1080p</td><td>1080 × 1920</td><td>Portrait / mobile</td></tr>
          </table>
        </div>

        <div class="feature">
          <div class="feature-name">Frame Rate &amp; Quality</div>
          <p>Output is rendered at <strong>24 fps</strong>. Text supersampling (100–200%) renders captions at higher resolution before scaling down, producing sharper caption edges at the cost of slightly longer render times.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Output File</div>
          <p>The finished video is saved as an MP4 file. You choose the destination path before rendering begins. The file is fully mixed: video, voice, music, binaural, and source audio are all combined into a single output.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Configurable Paths</div>
          <p>Both the video download path and the cache location can be set yourself, which is handy on machines with custom storage layouts or external drives.</p>
        </div>
      </div>

      <!-- 9. Premium -->
      <div class="section" id="premium">
        <div class="section-title">Premium Features</div>

        <div class="feature feature-hero">
          <div class="feature-name">✦ What's Included</div>
          <p>A premium license unlocks the full version of HypnoMaker without restrictions.</p>
          <ul>
            <li><strong>PMV Mode</strong>: the full beat-synced music video pipeline</li>
            <li><strong>Full Script Library</strong>: all 102 built-in hypno scripts</li>
            <li><strong>Custom Script Editor</strong>: write and save your own scripts</li>
            <li><strong>Bring Your Own API Key</strong>: use your own ElevenLabs or Fish Audio key with any voice in their libraries</li>
            <li><strong>Full Customization</strong>: fine-tune every aspect of your video: fonts, timing, transitions, filters, audio levels, and more</li>
            <li><strong>All future updates</strong>: premium features added in future versions</li>
          </ul>
        </div>
      </div>

      <div class="cl-footer">
        Thank you for your support. <strong>HypnoMaker</strong> keeps getting deeper.<br>
        <a href="https://hypnomaker.itch.io/hypnomaker" target="_blank" rel="noopener" style="color:#ff3d82;font-style:normal;font-weight:700;text-decoration:underline;">Download on itch.io →</a>
      </div>

    </div>
  </div>
</template>

<script setup>
// static page
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.cl-wrap {
  min-height: 100vh;
  padding-top: 100px;
}

.container {
  max-width: 720px;
  margin: 0 auto;
  padding: 20px 32px 80px;
}

.header {
  text-align: center;
  margin-bottom: 52px;
}

.version-badge {
  display: inline-block;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #ff3d82;
  border: 1.5px solid #ff85b0;
  border-radius: 20px;
  padding: 4px 18px;
  margin-bottom: 18px;
  background: #fff0f5;
}

h1 {
  font-family: 'Pacifico', cursive;
  font-size: clamp(32px, 6vw, 50px);
  font-weight: 400;
  color: #ff3d82;
  letter-spacing: 0.02em;
  margin-bottom: 8px;
  text-shadow: 2px 3px 0px rgba(255, 100, 150, 0.14);
}

.subtitle {
  color: #b06080;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.divider {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 36px 0 28px;
}
.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1.5px;
}
.divider::before { background: linear-gradient(to right, transparent, #ffd6e7); }
.divider::after  { background: linear-gradient(to left, transparent, #ffd6e7); }
.divider-glyph { color: #ff85b0; font-size: 16px; }

.section { margin-bottom: 38px; }

.feature, .toc, .fix-list { text-align: left; }

.section-title {
  font-family: 'Pacifico', cursive;
  font-size: 15px;
  font-weight: 400;
  color: #ff3d82;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.section-title::after {
  content: '';
  flex: 1;
  height: 1.5px;
  background: #ffd6e7;
}

.feature {
  background: #fff5f8;
  border: 1.5px solid #ffd6e7;
  border-left: 4px solid #ff85b0;
  border-radius: 10px;
  padding: 18px 22px;
  margin-bottom: 12px;
  transition: border-left-color 0.25s, box-shadow 0.25s;
}
.feature:hover {
  border-left-color: #ff3d82;
  box-shadow: 0 4px 18px rgba(255, 80, 140, 0.09);
}

.feature-name {
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #ff3d82;
  margin-bottom: 6px;
}

.feature p {
  color: #3d1a26;
  font-size: 16px;
  line-height: 1.65;
  margin-top: 6px;
}

.feature ul {
  list-style: none;
  margin-top: 10px;
  padding: 0;
}
.feature ul li {
  color: #b06080;
  font-size: 15px;
  padding: 3px 0 3px 20px;
  position: relative;
}
.feature ul li::before {
  content: '✦';
  position: absolute;
  left: 0;
  color: #ff85b0;
  font-size: 11px;
  top: 5px;
}
.feature ul li strong { color: #e0306e; }
.feature ul li em { color: #3d1a26; font-style: italic; }

.feature-hero {
  border-left: 4px solid #ff3d82;
  background: linear-gradient(135deg, #fff5f8 0%, #ffe8f2 100%);
  box-shadow: 0 6px 24px rgba(255, 80, 140, 0.10);
}
.feature-hero .feature-name { font-size: 14px; letter-spacing: 0.10em; }

.hero-note {
  margin-top: 10px;
  color: #b06080;
  font-style: italic;
  font-size: 15px;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.tag {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #ff3d82;
  background: #fff0f5;
  border: 1.5px solid #ffd6e7;
  border-radius: 20px;
  padding: 3px 12px;
}
.tag-premium {
  color: #b06080;
  background: #ffe8f2;
  border-color: #ff85b0;
}

.option-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 14px;
}
.option-table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #ff3d82;
  border-bottom: 1.5px solid #ffd6e7;
  padding: 6px 8px 8px;
}
.option-table td {
  padding: 8px;
  border-bottom: 1px solid #ffd6e7;
  color: #3d1a26;
  vertical-align: top;
}
.option-table tr:last-child td { border-bottom: none; }
.option-table td:first-child {
  font-weight: 600;
  color: #b06080;
  width: 38%;
  white-space: nowrap;
}

.note {
  background: #fff0f5;
  border: 1.5px solid #ffd6e7;
  border-left: 4px solid #ff85b0;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 10px;
  font-size: 14px;
  color: #b06080;
  line-height: 1.6;
}
.note strong { color: #ff3d82; }

.fix-list {
  list-style: none;
  background: #fff5f8;
  border: 1.5px solid #ffd6e7;
  border-radius: 10px;
  padding: 4px 22px;
}
.fix-list li {
  padding: 8px 0 8px 24px;
  border-bottom: 1px solid #ffd6e7;
  position: relative;
  font-size: 16px;
  color: #3d1a26;
}
.fix-list li:last-child { border-bottom: none; }
.fix-list li::before {
  content: '✦';
  position: absolute;
  left: 0;
  color: #ff85b0;
  font-size: 11px;
  top: 11px;
}

.toc {
  background: #fff5f8;
  border: 1.5px solid #ffd6e7;
  border-radius: 10px;
  padding: 18px 22px;
  margin-bottom: 36px;
}
.toc-title {
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: #ff3d82;
  margin-bottom: 10px;
}
.toc ol {
  padding-left: 18px;
  margin: 0;
}
.toc li {
  font-size: 14px;
  padding: 3px 0;
}
.toc a {
  color: #b06080;
  text-decoration: none;
  transition: color 0.2s;
}
.toc a:hover { color: #ff3d82; text-decoration: underline; }

.cl-footer {
  text-align: center;
  margin-top: 56px;
  padding-top: 28px;
  border-top: 1.5px solid #ffd6e7;
  color: #b06080;
  font-size: 15px;
  font-style: italic;
}
.cl-footer strong {
  color: #ff3d82;
  font-style: normal;
  font-weight: 700;
}

@media (max-width: 600px) {
  .container { padding: 16px 16px 60px; }
  .feature    { padding: 14px 16px; }
  .fix-list   { padding: 4px 16px; }
  .toc        { padding: 14px 16px; }
  .header     { margin-bottom: 24px; }
  .divider    { margin: 16px 0 16px; }
}
</style>